import React, { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Calendar, MapPin, ArrowRight, CheckCircle, Clock, XCircle, AlertCircle, Sparkles } from "lucide-react";
import "./VendorEvents.css";

const STATUS_META = {
  approved: { label: "Approved",     tone: "approved", icon: CheckCircle },
  accepted: { label: "Approved",     tone: "approved", icon: CheckCircle },
  pending:  { label: "Under review", tone: "pending",  icon: Clock },
  denied:   { label: "Not selected", tone: "denied",   icon: XCircle },
  on_hold:  { label: "On hold",      tone: "hold",      icon: AlertCircle },
};

const VendorEvents = () => {
  const { vendor } = useOutletContext();
  const [events, setEvents]             = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    if (!vendor?.id) { setLoading(false); return; }

    const load = async () => {
      const [
        { data: eventRows, error: eventErr },
        { data: appRows, error: appErr },
      ] = await Promise.all([
        supabase
          .from("events")
          .select(`
            id,
            name,
            start_date,
            end_date,
            location,
            event_key,
            banner_url,
            theme_color,
            short_description,
            category,
            applications_close_at,
            status
          `)
          .order("start_date", { ascending: true }),

        supabase
          .from("vendor_applications")
          .select("id,event_id,status")
          .eq("vendor_id", vendor.id),
      ]);

      if (eventErr) console.error("Events:", eventErr);
      if (appErr) console.error("Applications:", appErr);

      setEvents(eventRows || []);
      setApplications(appRows || []);
      setLoading(false);
    };

    load();
  }, [vendor?.id]);

  if (loading) return <div className="ve-loading">Loading events…</div>;

  const eventsById = Object.fromEntries(events.map((e) => [e.id, e]));
  const applicationFor = (eventId) => applications.find((a) => a.event_id === eventId);

  // An event is genuinely open for applications when the events table says
  // so AND the close date (if set) hasn't passed — the status field is the
  // primary signal, the date is a secondary guard.
  const isOpenForApplications = (event) => {
    if (event.status !== "applications_open") return false;
    if (!event.applications_close_at) return true;
    return new Date(event.applications_close_at) >= new Date();
  };

  const daysUntil = (date) => {
    const diff = Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
    if (diff <= 0) return "Happening now";
    if (diff === 1) return "Tomorrow";
    return `${diff} days away`;
  };

  const eventLink = (event) => `/vendor/events/${event?.event_key || event?.id}`;

  const today = new Date();
  const upcomingEvents = events.filter(
    (event) => new Date(event.end_date || event.start_date) >= today
  );
  const pastEvents = events.filter(
    (event) => new Date(event.end_date || event.start_date) < today
  );

  // The big apply card targets the next event genuinely open for
  // applications — not just the chronologically nearest one, which might
  // still be in draft or already closed.
  const featuredEvent = upcomingEvents.find(isOpenForApplications) || null;

  // ── Priority cards ──
  const appsWithEvent = applications.map((a) => ({ ...a, event: eventsById[a.event_id] }));

  // Only "you're in, event is live" cards sit above the apply card —
  // that's the one thing more urgent than "go apply."
  const liveApprovedApps = appsWithEvent.filter(
    (a) => (a.status === "approved" || a.status === "accepted") && a.event?.status === "live"
  );

  // Denied/on-hold notices are informational, not urgent — they belong
  // below the apply card so they don't bury the thing vendors should
  // actually act on.
  const attentionApps = appsWithEvent.filter(
    (a) => a.status === "denied" || a.status === "on_hold"
  );

  const renderCta = (event, { primaryClass = "ve-btn ve-btn--primary" } = {}) => {
    const app = applicationFor(event.id);

    if (app) {
      const status = STATUS_META[app.status] || STATUS_META.pending;
      if (status.tone === "approved") {
        return (
          <Link to={eventLink(event)} className={primaryClass}>
            Continue <ArrowRight size={14} />
          </Link>
        );
      }
      if (status.tone === "pending") {
        return <span className="ve-waiting-note">We'll notify you once there's a decision.</span>;
      }
      if (status.tone === "hold") {
        return (
          <Link to={eventLink(event)} className={primaryClass}>
            View details <ArrowRight size={14} />
          </Link>
        );
      }
      return <span className="ve-waiting-note">Not selected for this event.</span>;
    }

    if (!isOpenForApplications(event)) {
      return <span className="ve-closed">Applications Closed</span>;
    }

    return (
      <Link to={`/vendor/apply/${event.event_key || event.id}`} className={primaryClass}>
        Apply now <ArrowRight size={14} />
      </Link>
    );
  };

  // Everything else, excluding whichever event is currently featured.
  const otherUpcomingEvents = upcomingEvents.filter((e) => e.id !== featuredEvent?.id);

  return (
    <div className="ve-page">
      <div className="ve-header">
        <h1 className="ve-title">Events</h1>
        <p className="ve-subtitle">Apply for upcoming events or check the status of ones you've applied to.</p>
      </div>

      {/* ── Top priority: live event, vendor already approved ── */}
      {liveApprovedApps.length > 0 && (
        <div className="ve-priority-section">
          {liveApprovedApps.map((app) => (
            <div key={app.id} className="ve-priority-card ve-priority-card--live">
              <div className="ve-priority-top">
                <CheckCircle size={18} aria-hidden="true" />
                <span>{app.event?.name || "Event"} is live</span>
              </div>
              <p>Head to your workspace for your spot, documents, and payment status.</p>
              <Link to={eventLink(app.event)} className="ve-btn ve-btn--primary">
                Enter workspace <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>
      )}

      {events.length === 0 ? (
        <div className="ve-empty">No events are open right now — check back soon.</div>
      ) : (
        <>
          {/* ── Featured apply card ── */}
          {featuredEvent && (
            <div
              className="ve-featured"
              style={featuredEvent.banner_url ? { backgroundImage: `url(${featuredEvent.banner_url})` } : undefined}
            >
              <div className="ve-featured-overlay">
                <div className="ve-featured-eyebrow">
                  <Sparkles size={14} />
                  <span>Applications open</span>
                </div>

                {featuredEvent.category && (
                  <span className="ve-category" style={{ background: featuredEvent.theme_color || "#fa7921" }}>
                    {featuredEvent.category}
                  </span>
                )}

                <h2>{featuredEvent.name}</h2>
                {featuredEvent.short_description && <p>{featuredEvent.short_description}</p>}

                <div className="ve-featured-meta">
                  {featuredEvent.start_date && (
                    <span><Calendar size={15} /> {new Date(featuredEvent.start_date).toLocaleDateString()}</span>
                  )}
                  {featuredEvent.location && <span><MapPin size={15} /> {featuredEvent.location}</span>}
                  {featuredEvent.start_date && (
                    <span className="ve-countdown">{daysUntil(featuredEvent.start_date)}</span>
                  )}
                </div>

                {renderCta(featuredEvent)}
              </div>
            </div>
          )}

        

          {otherUpcomingEvents.length > 0 && (
            <>
              <h2 className="ve-section-title">More Events</h2>
              <div className="ve-grid">
                {otherUpcomingEvents.map((event) => (
                  <div key={event.id} className="ve-card">
                    {event.banner_url && (
                      <img src={event.banner_url} className="ve-card-image" alt={event.name} />
                    )}
                    <div className="ve-card-body">
                      <h3>{event.name}</h3>
                      {event.short_description && <p>{event.short_description}</p>}
                      <div className="ve-card-meta">
                        {event.start_date && (
                          <span><Calendar size={14} /> {new Date(event.start_date).toLocaleDateString()}</span>
                        )}
                        {event.location && <span><MapPin size={14} /> {event.location}</span>}
                      </div>
                      {renderCta(event, { primaryClass: "ve-btn ve-btn--secondary" })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {pastEvents.length > 0 && (
            <>
              <h2 className="ve-section-title">Past Events</h2>
              <div className="ve-grid">
                {pastEvents.map((event) => (
                  <div key={event.id} className="ve-card ve-card--past">
                    {event.banner_url && (
                      <img src={event.banner_url} className="ve-card-image" alt={event.name} />
                    )}
                    <div className="ve-card-body">
                      <h3>{event.name}</h3>
                      {event.short_description && <p>{event.short_description}</p>}
                      <span className="ve-finished">Completed</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default VendorEvents;